import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useLocation, useRouteLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { RootLoaderData } from '~/root';
import { PillButton } from '~/routes/community/_components/ui/PillButton';
import toast from '~/utils/toast';

import Button from '../Button';
import IosAddIcon from './IosAddIcon';
import { IosShareIcon } from './IosShareIcon';
import { isAndroidDevice } from './isAndroidDevice';
import { isIosDevice } from './isIosDevice';
import { isPublicationHomePath, PWA_PROMPT_STORAGE_KEYS } from './pwaPromptConfig';
import { useDeferredInstallPrompt } from './useDeferredInstallPrompt';
import { usePromptDismissal } from './usePromptDismissal';
import { usePwaInstalled } from './usePwaInstalled';

export type PwaPromptBranding = {
  name: string;
  logoUrl: string;
  subtitle?: string;
};

type InstallPromptProps = {
  debug?: boolean;
  canShowOnPath?: (pathname: string) => boolean;
  storageKey?: string;
  branding?: PwaPromptBranding;
  variant?: 'default' | 'community';
};

type InstallStepProps = {
  icon: ReactNode;
  label: string;
  hint?: string;
};

const InstallStep = ({ icon, label, hint }: InstallStepProps) => (
  <li className="flex items-center gap-4">
    <span className="flex w-7 shrink-0 justify-center">{icon}</span>
    <span>
      <span className="block font-semibold">{label}</span>
      {hint && <span className="block text-sm opacity-60">{hint}</span>}
    </span>
  </li>
);

const InstallPrompt = ({
  debug = false,
  canShowOnPath = isPublicationHomePath,
  storageKey = PWA_PROMPT_STORAGE_KEYS.publicationInstall,
  branding,
  variant = 'default',
}: InstallPromptProps) => {
  return (
    <ClientOnly>
      {() => (
        <InstallPromptImpl
          debug={debug}
          canShowOnPath={canShowOnPath}
          storageKey={storageKey}
          branding={branding}
          variant={variant}
        />
      )}
    </ClientOnly>
  );
};

function InstallPromptImpl({ debug = false, canShowOnPath, storageKey, branding, variant }: InstallPromptProps) {
  const location = useLocation();
  const canShowPrompt = canShowOnPath?.(location.pathname) ?? false;
  const isAndroid = isAndroidDevice();
  const isIos = isIosDevice();
  const isMobile = isAndroid || isIos;
  const isLoggedIn = useIsLoggedIn();
  const installedStorageKey =
    variant === 'community' ? PWA_PROMPT_STORAGE_KEYS.communityInstalled : PWA_PROMPT_STORAGE_KEYS.publicationInstalled;
  const { isPwaInstalled, markPwaInstalled } = usePwaInstalled(installedStorageKey);
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const publication = rootData?.site?.publication;
  const resolvedBranding: PwaPromptBranding = branding ?? {
    name: publication?.name ?? '',
    logoUrl: publication?.logo?.thumb?.url ?? '',
    subtitle: publication?.configured_domain,
  };
  const [promptShown, setPromptShown] = usePromptDismissal(storageKey ?? PWA_PROMPT_STORAGE_KEYS.publicationInstall);
  const deferredPrompt = useDeferredInstallPrompt();
  const manualOpenRef = useRef(false);
  const [showPrompt, setShowPrompt] = useState(
    canShowPrompt && !promptShown && !isPwaInstalled && isMobile && isLoggedIn,
  );
  const { t } = useTranslation();
  const isCommunityVariant = variant === 'community';

  const onInstall = async () => {
    const prompt = window.__DeferredPrompt__ ?? deferredPrompt;
    if (!prompt) return;

    setPromptShown(true);
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') {
      markPwaInstalled();
    }
    setShowPrompt(false);
  };

  const handleAppInstalled = useCallback(() => {
    toast.success(t('pwa.install_prompt.install_success'));
  }, [t]);

  useEffect(() => {
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [handleAppInstalled]);

  useEffect(() => {
    function handleShowInstallPrompt(e: Event) {
      e.preventDefault();
      manualOpenRef.current = true;
      setShowPrompt(true);
    }

    document.addEventListener('beehiiv:showInstallPrompt', handleShowInstallPrompt);
    return () => {
      document.removeEventListener('beehiiv:showInstallPrompt', handleShowInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (!isPwaInstalled) return;

    manualOpenRef.current = false;
    setShowPrompt(false);
  }, [isPwaInstalled]);

  useEffect(() => {
    if (manualOpenRef.current) return;

    const canAutoShow =
      canShowPrompt &&
      !promptShown &&
      !isPwaInstalled &&
      isMobile &&
      isLoggedIn &&
      (!isAndroid || Boolean(deferredPrompt));

    setShowPrompt(canAutoShow);
  }, [canShowPrompt, promptShown, isPwaInstalled, isMobile, isLoggedIn, isAndroid, deferredPrompt]);

  if (!resolvedBranding.logoUrl || !resolvedBranding.name) return null;
  if (!debug && !isAndroid && !isIos) return null;
  if (!showPrompt) return null;

  const dismissPrompt = () => {
    manualOpenRef.current = false;
    setShowPrompt(false);
    setPromptShown(true);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.5)]">
      <div
        className={`absolute bottom-0 w-full rounded-t-xl border p-6 ${
          isCommunityVariant
            ? 'border-community-primary/20 bg-community-bg text-community-text-on-bg'
            : 'bg-white text-gray-950'
        }`}
      >
        <div className="flex w-full items-center justify-between">
          <h1 className="text-2xl font-bold">{t('pwa.install_prompt.install_prompt')}</h1>
          <button type="button" aria-label="Close" onClick={dismissPrompt}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div
          className={`mt-6 flex gap-4 rounded border p-4 ${
            isCommunityVariant ? 'border-community-primary/20 bg-white/80 text-black' : 'border-gray-200 bg-gray-100'
          }`}
        >
          <img
            src={resolvedBranding.logoUrl}
            alt={resolvedBranding.name}
            className="h-16 w-16 overflow-clip rounded-lg"
          />
          <div>
            <h2 className="text-lg font-semibold">{resolvedBranding.name}</h2>
            {resolvedBranding.subtitle ? <p>{resolvedBranding.subtitle}</p> : null}
          </div>
        </div>

        {isAndroid && (
          <div className="mx-6 my-8 flex flex-col items-center">
            {isCommunityVariant ? (
              <PillButton type="button" variant="primary" onClick={onInstall} disabled={!deferredPrompt}>
                {t('pwa.install_prompt.install_cta')}
              </PillButton>
            ) : (
              <Button type="button" variant="primary" onClick={onInstall} disabled={!deferredPrompt}>
                {t('pwa.install_prompt.install_cta')}
              </Button>
            )}
            <p className="mt-4">{t('pwa.install_prompt.install_sub_cta')}</p>
          </div>
        )}
        {isIos && (
          <ul className="mx-2 mb-4 mt-6 space-y-5">
            <InstallStep
              icon={<IosShareIcon className="h-6 w-6" />}
              label={t('pwa.install_prompt.step_share')}
              hint={t('pwa.install_prompt.step_more')}
            />
            <InstallStep icon={<IosAddIcon className="h-6 w-6" />} label={t('pwa.install_prompt.step_add')} />
            <InstallStep icon={<CheckIcon className="h-6 w-6" />} label={t('pwa.install_prompt.step_confirm')} />
            <InstallStep
              icon={
                <img
                  src={resolvedBranding.logoUrl}
                  alt={resolvedBranding.name}
                  className="h-6 w-6 overflow-clip rounded-lg"
                />
              }
              label={t('pwa.install_prompt.step_find')}
            />
          </ul>
        )}
      </div>
    </div>
  );
}

export default InstallPrompt;
